import type { AgeCategory, ClientDTO, ClientType, Language } from '@brainkey/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { Pencil, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { CreateClientModal } from '../components/CreateClientModal';
import { EditClientModal } from '../components/EditClientModal';
import { PageHeader } from '../components/PageHeader';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { staggerContainer, staggerItem } from '../components/ui/motionVariants';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import type { Column } from '../components/ui/Table';
import {
  ageLabel,
  clientTypeLabel,
  errorMessage,
  formatDate,
  fullName,
  languageLabel,
} from '../lib/format';
import { useClientsList, useDeleteClient } from '../lib/hooks/useClients';
import { useDebouncedValue } from '../lib/hooks/useDebouncedValue';

const PAGE_SIZE = 20;

function initials(c: ClientDTO): string {
  return ((c.firstName[0] ?? '') + (c.lastName[0] ?? '')).toUpperCase();
}

export function ClientsPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('clients:edit');
  const canDelete = hasPermission('clients:delete');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [languageFilter, setLanguageFilter] = useState<Language | ''>('');
  const [ageFilter, setAgeFilter] = useState<AgeCategory | ''>('');
  const [clientTypeFilter, setClientTypeFilter] = useState<ClientType | ''>('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientDTO | null>(null);
  const [deletingClient, setDeletingClient] = useState<ClientDTO | null>(null);
  const deleteClient = useDeleteClient();

  const debouncedSearch = useDebouncedValue(search.trim(), 350);
  const hasFilters =
    debouncedSearch !== '' || languageFilter !== '' || ageFilter !== '' || clientTypeFilter !== '';

  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      ...(debouncedSearch !== '' ? { q: debouncedSearch } : {}),
      ...(languageFilter !== '' ? { languagePref: languageFilter } : {}),
      ...(ageFilter !== '' ? { ageCategory: ageFilter } : {}),
      ...(clientTypeFilter !== '' ? { clientType: clientTypeFilter } : {}),
    }),
    [page, debouncedSearch, languageFilter, ageFilter, clientTypeFilter],
  );

  const { data, isLoading, isError, error, isFetching } = useClientsList(query);

  const columns: Column<ClientDTO>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (c) => (
        <div className="flex items-center gap-3">
          <span
            className="w-10 h-10 shrink-0 rounded-full bg-zinc-100 dark:bg-zinc-800 text-brand-600 dark:text-brand-400 font-medium text-sm flex items-center justify-center border border-zinc-200 dark:border-white/5"
            aria-hidden="true"
          >
            {initials(c)}
          </span>
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900 dark:text-zinc-100">{fullName(c)}</span>
            <span className="text-xs text-zinc-500">
              {c.email ?? 'No email'} · <span className="font-mono">{c.clientCode}</span>
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (c) => (
        <Badge tone={c.clientType === 'FOREIGN' ? 'info' : 'neutral'}>
          {clientTypeLabel(c.clientType)}
        </Badge>
      ),
    },
    {
      key: 'language',
      header: 'Language',
      render: (c) => <Badge tone="neutral">{languageLabel(c.languagePref)}</Badge>,
    },
    {
      key: 'age',
      header: 'Age group',
      render: (c) => (
        <span className="text-zinc-500 dark:text-zinc-400 text-sm">{ageLabel(c.ageCategory)}</span>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (c) => (
        <span className="text-zinc-500 dark:text-zinc-400 text-sm">{c.phone ?? '—'}</span>
      ),
    },
    {
      key: 'created',
      header: 'Added',
      render: (c) => (
        <span className="text-zinc-500 dark:text-zinc-400 text-sm tabular-nums">
          {formatDate(c.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (c) => (
        <div className="flex items-center gap-1.5">
          {canEdit && (
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-500 dark:hover:text-zinc-100 dark:hover:bg-white/10 transition-colors"
              aria-label={`Edit ${fullName(c)}`}
              onClick={(e) => {
                e.stopPropagation();
                setEditingClient(c);
              }}
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-rose-600 hover:bg-rose-50 dark:text-zinc-500 dark:hover:text-rose-400 dark:hover:bg-rose-500/10 transition-colors"
              aria-label={`Delete ${fullName(c)}`}
              onClick={(e) => {
                e.stopPropagation();
                setDeletingClient(c);
              }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const rows = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;

  const onSearchChange = (value: string): void => {
    setSearch(value);
    setPage(1); // new query resets to the first page
  };

  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={staggerContainer(0.1)}
      className="flex flex-col gap-6 pb-12"
    >
      <motion.div variants={staggerItem}>
        <PageHeader
          title="Clients"
          description="Manage the people you coach, their intake data, and their reports."
          actions={
            hasPermission('clients:create') ? (
              <Button onClick={() => setCreateOpen(true)}>New client</Button>
            ) : undefined
          }
        />
      </motion.div>

      <motion.div variants={staggerItem} className="flex flex-col md:flex-row gap-4 mb-2">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500 z-10" />
          <Input
            className="h-[42px] pl-11"
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search clients"
          />
        </div>
        <div className="flex gap-4 md:w-auto w-full">
          <div className="flex-1 md:w-48">
            <Select
              aria-label="Filter by language"
              value={languageFilter}
              onValueChange={(value) => {
                setLanguageFilter(value as Language | '');
                setPage(1);
              }}
              className="h-[42px]"
              options={[
                { value: '', label: 'All languages' },
                { value: 'EN', label: 'English' },
                { value: 'SI', label: 'Sinhala' },
                { value: 'TA', label: 'Tamil' },
              ]}
            />
          </div>
          <div className="flex-1 md:w-48">
            <Select
              aria-label="Filter by age group"
              value={ageFilter}
              onValueChange={(value) => {
                setAgeFilter(value as AgeCategory | '');
                setPage(1);
              }}
              className="h-[42px]"
              options={[
                { value: '', label: 'All age groups' },
                { value: 'CHILD', label: 'Child' },
                { value: 'TEEN', label: 'Teen' },
                { value: 'ADULT', label: 'Adult' },
                { value: 'SENIOR', label: 'Senior' },
              ]}
            />
          </div>
          <div className="flex-1 md:w-48">
            <Select
              aria-label="Filter by client type"
              value={clientTypeFilter}
              onValueChange={(value) => {
                setClientTypeFilter(value as ClientType | '');
                setPage(1);
              }}
              className="h-[42px]"
              options={[
                { value: '', label: 'All client types' },
                { value: 'LOCAL', label: 'Local' },
                { value: 'FOREIGN', label: 'Foreign' },
              ]}
            />
          </div>
        </div>
      </motion.div>

      <motion.div variants={staggerItem}>
        <AnimatePresence initial={false}>
          {isError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6"
            >
              <Alert tone="danger">{errorMessage(error, 'Could not load clients.')}</Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <Table
          columns={columns}
          rows={rows}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/clients/${c.id}`)}
          loading={isLoading}
          emptyTitle={hasFilters ? 'No matching clients' : 'No clients yet'}
          emptyHint={
            hasFilters
              ? 'Try a different search or filter.'
              : 'Create your first client to get started.'
          }
        />

        {total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <span className="text-sm text-zinc-500">
              Page {page} of {totalPages} · {total} client{total === 1 ? '' : 's'}
              {isFetching ? ' · updating…' : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      <CreateClientModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(client) => {
          setCreateOpen(false);
          navigate(`/clients/${client.id}`);
        }}
      />

      {editingClient && (
        <EditClientModal
          open={editingClient !== null}
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onUpdated={() => setEditingClient(null)}
        />
      )}

      <Modal
        open={deletingClient !== null}
        onClose={() => {
          if (deleteClient.isPending) return;
          setDeletingClient(null);
        }}
        title="Delete client"
        description={deletingClient ? `Archive ${fullName(deletingClient)}?` : undefined}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeletingClient(null)}
              disabled={deleteClient.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteClient.isPending}
              onClick={() => {
                if (!deletingClient) return;
                deleteClient.mutate(deletingClient.id, {
                  onSuccess: () => setDeletingClient(null),
                });
              }}
            >
              Delete client
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Alert tone="danger">
            This archives the client and hides them from the register. Their assessments, reports,
            questionnaires, and notes are kept but will no longer be reachable from here.
          </Alert>
          {deleteClient.isError && (
            <Alert tone="danger">
              {errorMessage(deleteClient.error, 'Could not delete the client.')}
            </Alert>
          )}
        </div>
      </Modal>
    </motion.div>
  );
}
